/**
 * @fileoverview Billing admin view (MOR-438)
 *
 * Owner-only billing engine v1 over `convex/admin/billing.ts`, in three tabs:
 * - Dashboard  — revenue, collection and payment-status analytics with
 *   drill-downs (BillingDashboard).
 * - Firms      — per-firm billing plans, Stripe wiring and the charge ledger
 *   (FirmsTab → ManageOrgDialog).
 * - Affiliates — partner agreements: declining rate schedules, gross-margin
 *   adjustments and per-period fee breakdowns (AffiliatesTab).
 *
 * All amounts are AUD; fees are entered ex-GST and GST is added on top.
 *
 * NEVER link this view (or its queries) anywhere a firm user could reach.
 */

import { useState } from "react";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { BillingDashboard } from "./BillingDashboard";
import { FirmsTab } from "./FirmsTab";
import { AffiliatesTab } from "./AffiliatesTab";
import { ManageOrgDialog } from "./ManageOrgDialog";
import { AgreementDialog } from "./AgreementDialog";
import { BreakdownDialog } from "./BreakdownDialog";
import type { Agreement } from "./billingFormat";

export function BillingAdminView() {
  const [tab, setTab] = useState("dashboard");
  const [managedOrgId, setManagedOrgId] = useState<Id<"organizations"> | null>(
    null,
  );
  const [editedAgreement, setEditedAgreement] = useState<
    Agreement | "new" | null
  >(null);
  const [breakdownAgreementId, setBreakdownAgreementId] =
    useState<Id<"affiliateAgreements"> | null>(null);

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex flex-col gap-6">
      <TabsList className="self-start">
        <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
        <TabsTrigger value="firms">Firms</TabsTrigger>
        <TabsTrigger value="affiliates">Affiliates</TabsTrigger>
      </TabsList>

      <TabsContent value="dashboard" className="mt-0">
        <BillingDashboard onSelectFirm={setManagedOrgId} />
      </TabsContent>

      <TabsContent value="firms" className="mt-0">
        <FirmsTab onManage={setManagedOrgId} />
      </TabsContent>

      <TabsContent value="affiliates" className="mt-0">
        <AffiliatesTab
          onEdit={setEditedAgreement}
          onBreakdown={setBreakdownAgreementId}
        />
      </TabsContent>

      {managedOrgId && (
        <ManageOrgDialog
          orgId={managedOrgId}
          onClose={() => setManagedOrgId(null)}
        />
      )}
      {editedAgreement && (
        <AgreementDialog
          agreement={editedAgreement === "new" ? null : editedAgreement}
          onClose={() => setEditedAgreement(null)}
        />
      )}
      {breakdownAgreementId && (
        <BreakdownDialog
          agreementId={breakdownAgreementId}
          onClose={() => setBreakdownAgreementId(null)}
        />
      )}
    </Tabs>
  );
}
